/**
 * OnboardingWizard — shown to new users on first visit when no products exist.
 * Guides through: Welcome → Business Setup → Add First Product → Go!
 * Dismissed: localStorage flag 'agri_onboarding_done'
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  X, ShoppingCart, Package, Building2, Zap, ChevronRight,
  Check, Store, BarChart3, Users, ArrowRight
} from 'lucide-react';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to AgriBooks!',
    subtitle: "Let's get your store ready in under 2 minutes.",
    icon: Zap,
    color: '#1A4D2E',
  },
  {
    id: 'product',
    title: 'Add your first product',
    subtitle: "Add one product so you can make your first sale.",
    icon: Package,
    color: '#C85A32',
  },
  {
    id: 'done',
    title: "You're all set!",
    subtitle: "Here's what you can do first.",
    icon: Check,
    color: '#477242',
  },
];

function QuickAddProductForm({ onDone }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: '', price: '', unit: 'pcs', category: 'General' });
  const [saving, setSaving] = useState(false);
  const units = ['pcs', 'kg', 'bag', 'btl', 'box', 'roll'];

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) { toast.error('Name and price are required'); return; }
    setSaving(true);
    try {
      await api.post('/products', {
        name: form.name.trim(),
        cost_price: parseFloat(form.price) * 0.7,
        prices: { retail: parseFloat(form.price) },
        unit: form.unit,
        category: form.category,
        active: true,
      });
      toast.success(`${form.name} added!`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add product');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3 mt-4">
      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1">Product Name</label>
        <input
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
          placeholder="e.g. Rice, NPK Fertilizer, Paracetamol..."
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          autoFocus
          data-testid="onboard-product-name"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Retail Price (₱)</label>
          <input
            type="number"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
            placeholder="0.00"
            value={form.price}
            onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
            data-testid="onboard-product-price"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Unit</label>
          <select
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
            value={form.unit}
            onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
          >
            {units.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all active:scale-95"
          style={{ backgroundColor: '#1A4D2E' }}
          data-testid="onboard-product-save"
        >
          {saving ? 'Adding…' : 'Add Product →'}
        </button>
        <button onClick={onDone} className="px-4 py-2.5 rounded-xl text-sm text-slate-500 hover:bg-slate-50">
          Skip
        </button>
      </div>
    </div>
  );
}

function DoneStep({ onClose }) {
  const navigate = useNavigate();
  const quickActions = [
    { label: 'Make a Sale',        icon: ShoppingCart, path: '/sales-new',    color: '#1A4D2E' },
    { label: 'View Products',       icon: Package,     path: '/products',      color: '#C85A32' },
    { label: 'Add Customers',       icon: Users,        path: '/customers',    color: '#477242' },
    { label: 'Go to Dashboard',     icon: BarChart3,    path: '/',             color: '#2D3728' },
  ];
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-2">
        {quickActions.map(a => (
          <button
            key={a.path}
            onClick={() => { onClose(); navigate(a.path); }}
            className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: a.color + '15' }}>
              <a.icon size={16} style={{ color: a.color }} />
            </div>
            <span className="text-sm font-semibold text-slate-800">{a.label}</span>
          </button>
        ))}
      </div>
      <button
        onClick={onClose}
        className="w-full py-2.5 rounded-xl text-sm font-bold text-white active:scale-95"
        style={{ backgroundColor: '#1A4D2E' }}
        data-testid="onboard-finish-btn"
      >
        Start using AgriBooks →
      </button>
    </div>
  );
}

export default function OnboardingWizard() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user || user.is_super_admin) return;
    // Already dismissed on the server (any device, ever) — never show again.
    if (user.onboarding_dismissed) return;
    // Skip for demo orgs (they already have full data seeded).
    if (user.subscription?.is_demo) return;
    // Fast local short-circuit so we don't flash on every navigation.
    if (localStorage.getItem('agri_onboarding_done')) return;

    let cancelled = false;
    (async () => {
      try {
        // Only a genuinely NEW / empty store should see the wizard. If the
        // account already has products, it's an established business — record
        // the dismissal server-side so it never bothers them again.
        const res = await api.get('/products', { params: { limit: 1 } });
        const data = res.data;
        const count = typeof data?.total === 'number'
          ? data.total
          : (Array.isArray(data) ? data.length : (data?.products?.length || 0));
        if (cancelled) return;
        if (count > 0) {
          persistDismiss(false); // silent — established account
          return;
        }
        setShow(true);
      } catch {
        // If the check fails, do nothing (don't risk nagging an existing user).
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Persist dismissal both locally (fast) and server-side (durable, per account).
  const persistDismiss = (closeUI = true) => {
    if (closeUI) setShow(false);
    localStorage.setItem('agri_onboarding_done', '1');
    api.post('/auth/onboarding/dismiss').catch(() => {});
  };

  const dismiss = () => persistDismiss(true);

  if (!show) return null;

  const StepIcon = STEPS[step].icon;
  const isLast = step === STEPS.length - 1;
  const progressPct = Math.round(((step) / (STEPS.length - 1)) * 100);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="onboarding-wizard"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div
          className="px-6 pt-6 pb-4 relative"
          style={{ backgroundColor: STEPS[step].color + '10', borderBottom: `1px solid ${STEPS[step].color}20` }}
        >
          <button onClick={dismiss} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-black/5">
            <X size={16} className="text-slate-400" />
          </button>
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: STEPS[step].color }}
            >
              <StepIcon size={22} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Step {step + 1} of {STEPS.length}
              </p>
              <h3 className="font-black text-slate-900 text-base leading-tight">{STEPS[step].title}</h3>
            </div>
          </div>
          <p className="text-slate-500 text-xs mt-2">{STEPS[step].subtitle}</p>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%`, backgroundColor: STEPS[step].color }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {step === 0 && (
            <div className="space-y-3">
              {[
                { icon: ShoppingCart, text: 'Make cash, credit, and digital sales instantly' },
                { icon: Package,      text: 'Track inventory across products and branches' },
                { icon: BarChart3,    text: 'Daily close reports, fund management, and Z-Reports' },
                { icon: Store,        text: 'Works offline — no internet needed at the counter' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center shrink-0">
                    <Icon size={15} style={{ color: '#1A4D2E' }} />
                  </div>
                  <p className="text-slate-700 text-sm">{text}</p>
                </div>
              ))}
              <button
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white mt-3 active:scale-95"
                style={{ backgroundColor: '#1A4D2E' }}
                onClick={() => setStep(1)}
                data-testid="onboard-next-btn"
              >
                Let's set up your store →
              </button>
              <button
                className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-1 py-1"
                onClick={dismiss}
                data-testid="onboard-dont-show-again-btn"
              >
                Don't show this again
              </button>
            </div>
          )}
          {step === 1 && (
            <QuickAddProductForm onDone={() => setStep(2)} />
          )}
          {step === 2 && (
            <DoneStep onClose={dismiss} />
          )}
        </div>
      </div>
    </div>
  );
}
